import { onBoardPawns } from './board';
import { curioActions } from './ledger/curios';
import { holds } from './ledger/state';
import {
  canRecover,
  checkEpitaphReplay,
  epitaphPlaysLeft,
  linkOptionsForCard,
  type LinkOption,
} from './rules';
import type { Action, LinkTarget, MatchState, PlayLinkAction, Side, Square } from './types';

/** The PLAY_LINK action for a link option. */
export function linkAction(side: Side, lo: LinkOption): PlayLinkAction {
  const target: LinkTarget = lo.trick
    ? { trick: lo.trick.target }
    : lo.option.move.type === 'hops' || lo.option.move.type === 'knights'
      ? { hops: lo.option.landings }
      : { to: lo.option.landings[0] as Square };
  return lo.wasWild
    ? {
        type: 'PLAY_LINK',
        side,
        cardId: lo.cardId,
        pawnId: lo.pawnId,
        target,
        wild: { face: lo.face, collateralPawnId: lo.collateralPawnId as string },
      }
    : { type: 'PLAY_LINK', side, cardId: lo.cardId, pawnId: lo.pawnId, target };
}

/** Every legal link option for the side's next link (§16.6). */
export function legalLinkOptions(s: MatchState, side: Side): LinkOption[] {
  if (s.decider !== side || (s.phase !== 'ACTION' && s.phase !== 'CHAIN')) return [];
  const out: LinkOption[] = [];
  for (const cardId of s.players[side].hand) out.push(...linkOptionsForCard(s, side, cardId));
  return out;
}

/**
 * Every legal action for `side` in the current phase (§16.6), including CONCEDE,
 * which either side may use in any phase except MATCH_OVER.
 */
export function legalActions(s: MatchState, side: Side): Action[] {
  if (s.phase === 'MATCH_OVER') return [];
  const out: Action[] = [];
  if (side === s.decider) {
    const pl = s.players[side];
    switch (s.phase) {
      case 'CHOOSE_STARTER':
        out.push(
          { type: 'CHOOSE_STARTER', side, starter: 'S' },
          { type: 'CHOOSE_STARTER', side, starter: 'N' },
        );
        break;
      case 'TURN_START':
        out.push({ type: 'PASS', side }, { type: 'CONTINUE', side });
        out.push(...curioActions(s, side));
        break;
      case 'ACTION': {
        for (const lo of legalLinkOptions(s, side)) out.push(linkAction(side, lo));
        const seance = s.ledger !== undefined && holds(s, side, 'seance-table');
        if (epitaphPlaysLeft(s, side))
          for (const epitaph of pl.epitaphs)
            for (const pawn of onBoardPawns(s.pawns, side))
              if (checkEpitaphReplay(s, side, epitaph, pawn.id).ok) {
                out.push({ type: 'PLAY_EPITAPH', side, pawnId: pawn.id, epitaphId: epitaph.id });
                if (seance)
                  out.push({
                    type: 'PLAY_EPITAPH',
                    side,
                    pawnId: pawn.id,
                    epitaphId: epitaph.id,
                    chain: true,
                  });
              }
        if (canRecover(s, side))
          for (const cardId of pl.hand) out.push({ type: 'RECOVER', side, cardId });
        out.push(...curioActions(s, side));
        break;
      }
      case 'CHAIN':
        for (const lo of legalLinkOptions(s, side)) out.push(linkAction(side, lo));
        out.push({ type: 'END_CHAIN', side });
        out.push(...curioActions(s, side));
        break;
      case 'CHOOSE_INSCRIPTION':
        out.push(
          { type: 'CHOOSE_INSCRIPTION', side, replace: false },
          { type: 'CHOOSE_INSCRIPTION', side, replace: true },
        );
        break;
      case 'CHOOSE_EPITAPH': {
        const choice = s.pendingEpitaphChoice;
        if (choice)
          for (const e of choice.candidates)
            if (!choice.kept.includes(e.id))
              out.push({ type: 'CHOOSE_EPITAPH', side, keepEpitaphId: e.id });
        break;
      }
    }
  }
  out.push({ type: 'CONCEDE', side });
  return out;
}
